import React, { useEffect, useMemo, useState } from "react";
import { Check, Flame, Truck, Users, Clock, Sparkles, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { fmtMXN } from "@/lib/format";

const HERO_IMG = "https://images.unsplash.com/photo-1555939594-58d7cb561ad1?auto=format&fit=crop&w=2000&q=85";

const STATES_MX = [
  "CDMX", "EDOMEX", "Querétaro", "Puebla", "Hidalgo", "Morelos", "Tlaxcala",
  "Jalisco", "Nuevo León", "Guanajuato", "Sonora", "Veracruz", "Otro",
];

export default function ParrilladasPage() {
  const [data, setData] = useState(null);
  const [pkgId, setPkgId] = useState("amonos-recio");
  const [comp, setComp] = useState("5");
  const [personas, setPersonas] = useState(30);
  const [form, setForm] = useState({
    contact_name: "", contact_email: "", contact_phone: "",
    event_date: "", event_time: "18:00",
    location_state: "CDMX", location_address: "",
    extras: [], message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/parrilladas/packages").then((r) => setData(r.data));
  }, []);

  const pkg = data?.packages.find((p) => p.id === pkgId);
  const pricePerPerson = pkg?.prices?.[comp] || 0;
  const estimatedTotal = pricePerPerson * personas;

  const needsViaticos = useMemo(() => {
    const s = (form.location_state || "").toUpperCase();
    return !["CDMX", "EDOMEX"].includes(s);
  }, [form.location_state]);

  const toggleExtra = (id) => {
    setForm((f) => ({
      ...f,
      extras: f.extras.includes(id) ? f.extras.filter((e) => e !== id) : [...f.extras, id],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.contact_name || !form.contact_email || !form.contact_phone) {
      setErr("Llena tus datos de contacto, compare.");
      return;
    }
    if (personas < (data?.min_personas || 20)) {
      setErr(`El mínimo es ${data?.min_personas || 20} personas.`);
      return;
    }
    setSubmitting(true);
    try {
      const { data: res } = await api.post("/parrilladas/quote", {
        package_id: pkgId,
        complementos_count: parseInt(comp),
        persona_count: personas,
        ...form,
      });
      setResult(res);
    } catch (er) {
      setErr(er.response?.data?.detail || "Error al enviar la solicitud");
    } finally {
      setSubmitting(false);
    }
  };

  if (!data) {
    return (
      <div className="min-h-screen bg-arre-black flex items-center justify-center">
        <div className="font-display text-2xl text-white/50 tracking-wider">Cargando parrilladas...</div>
      </div>
    );
  }

  return (
    <div className="bg-arre-black" data-testid="parrilladas-page">
      {/* HERO */}
      <section className="relative min-h-[80vh] flex items-end overflow-hidden grain">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('${HERO_IMG}')` }} />
        <div className="absolute inset-0 bg-gradient-to-t from-arre-black via-arre-black/80 to-arre-black/40" />
        <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12 pb-20 pt-40 w-full">
          <div className="inline-flex items-center gap-2 px-3 py-1 border border-arre-red/60 bg-arre-red/10 text-arre-red text-[10px] tracking-[0.35em] uppercase mb-6">
            <Flame className="w-3 h-3" /> Parrilladas y eventos
          </div>
          <h1 className="font-display text-6xl sm:text-8xl lg:text-[8.5rem] leading-[0.85] tracking-wider uppercase text-white text-glow-red max-w-5xl">
            El sabor del<br />norte en<br /><span className="text-arre-red">tu evento.</span>
          </h1>
          <p className="font-serif-display italic text-2xl md:text-3xl text-white/85 mt-6 max-w-2xl leading-snug">
            Carnita asada al carbón, una cheve fría y experiencia 100% sonorense en tu casa o oficina. Nosotros nos encargamos de todo, compare.
          </p>
          <div className="flex flex-wrap gap-4 mt-8 text-xs tracking-[0.25em] uppercase text-white/80">
            <span className="flex items-center gap-2"><Users className="w-4 h-4 text-arre-red" /> Mínimo {data.min_personas} personas</span>
            <span className="flex items-center gap-2"><Clock className="w-4 h-4 text-arre-red" /> {data.service_hours} horas de servicio</span>
            <span className="flex items-center gap-2"><Truck className="w-4 h-4 text-arre-red" /> CDMX y EdoMex · Otros estados con viáticos</span>
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="py-20 border-y border-arre-border">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-4 gap-6">
          {[
            { i: Sparkles, t: "Calidad 100% Sonora", d: "Cortes High Choice y Prime, orgullosamente sonorenses." },
            { i: Flame, t: "Maestros de la parrilla", d: "Personal experto que genera la experiencia auténtica del norte." },
            { i: Check, t: "Ingredientes frescos", d: "Acompañamientos auténticos y de la mejor calidad." },
            { i: Truck, t: "Llegamos a ti", d: "Setup completo, parrilla, carbón y logística incluidos." },
          ].map((b, i) => (
            <div key={i} className="border border-arre-border bg-arre-surface p-6">
              <b.i className="w-6 h-6 text-arre-red mb-3" strokeWidth={1.5} />
              <div className="font-display text-xl tracking-wider uppercase text-white">{b.t}</div>
              <p className="text-sm text-white/60 mt-2">{b.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PACKAGES */}
      <section className="py-24 px-6 md:px-12 max-w-[1600px] mx-auto" data-testid="parrilladas-packages">
        <div className="text-xs tracking-[0.35em] uppercase text-arre-red mb-3">⟡ Paquetes</div>
        <h2 className="font-display text-5xl md:text-7xl tracking-wider uppercase text-white leading-none mb-3">
          Elige tu<br /><span className="text-arre-red italic font-serif-display normal-case tracking-tight">parrillada</span>
        </h2>
        <p className="text-white/60 text-base max-w-xl mb-12">
          Precio por persona. Cada paquete tiene 3 niveles según los complementos que incluya.
        </p>

        <div className="space-y-12">
          {["high-choice", "prime"].map((tier) => (
            <div key={tier}>
              <div className="font-display text-2xl tracking-[0.3em] uppercase text-arre-red mb-6">
                ⟡ {tier === "high-choice" ? "High Choice" : "Prime"}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {data.packages.filter((p) => p.tier === tier).map((p) => (
                  <div
                    key={p.id}
                    className={`relative bg-arre-surface border ${p.popular ? "border-arre-red" : "border-arre-border"} p-7`}
                    data-testid={`parr-pkg-${p.id}`}
                  >
                    {p.popular && (
                      <div className="absolute -top-3 left-7 bg-arre-red text-white text-[10px] tracking-[0.3em] uppercase px-3 py-1 font-bold">
                        El más completo
                      </div>
                    )}
                    <div className="font-display text-3xl tracking-wider uppercase text-white">{p.name}</div>
                    <p className="text-white/60 text-sm mt-2 mb-5">{p.tagline}</p>

                    <div className="text-[10px] tracking-[0.3em] uppercase text-white/50 mb-2">Cortes a elegir</div>
                    <div className="flex flex-wrap gap-1.5 mb-5">
                      {p.cortes.map((c) => (
                        <span key={c} className="text-[11px] tracking-wide bg-arre-black border border-arre-border text-white/80 px-2 py-1">{c}</span>
                      ))}
                    </div>

                    <div className="grid grid-cols-3 gap-2 mt-5">
                      {["3", "5", "8"].map((n) => (
                        <button
                          key={n}
                          onClick={() => { setPkgId(p.id); setComp(n); document.getElementById("parr-quote-form")?.scrollIntoView({ behavior: "smooth" }); }}
                          className={`border p-3 transition text-left ${
                            pkgId === p.id && comp === n
                              ? "border-arre-red bg-arre-red/10"
                              : "border-arre-border hover:border-white/40 bg-arre-black"
                          }`}
                          data-testid={`tier-${p.id}-${n}`}
                        >
                          <div className="text-[10px] tracking-widest uppercase text-white/50">{n} complementos</div>
                          <div className="font-display text-2xl text-white mt-1">{fmtMXN(p.prices[n])}</div>
                          <div className="text-[10px] text-white/40 mt-0.5">por persona</div>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* INCLUDED + COMPLEMENTS + EXTRAS */}
      <section className="py-20 bg-arre-surface/30 border-y border-arre-border">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div>
            <div className="text-xs tracking-[0.35em] uppercase text-arre-red mb-3">⟡ Siempre incluido</div>
            <h3 className="font-display text-3xl tracking-wider uppercase text-white mb-5">En todos los paquetes</h3>
            <ul className="space-y-2">
              {data.included_always.map((i) => (
                <li key={i} className="flex items-center gap-2 text-white/85">
                  <Check className="w-4 h-4 text-arre-red flex-shrink-0" /> {i}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="text-xs tracking-[0.35em] uppercase text-arre-red mb-3">⟡ Complementos disponibles</div>
            <h3 className="font-display text-3xl tracking-wider uppercase text-white mb-5">Elige los tuyos</h3>
            <div className="flex flex-wrap gap-2">
              {data.complementos.map((c) => (
                <span key={c} className="text-xs tracking-wide bg-arre-black border border-arre-border text-white/80 px-3 py-1.5">{c}</span>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs tracking-[0.35em] uppercase text-arre-red mb-3">⟡ Extras</div>
            <h3 className="font-display text-3xl tracking-wider uppercase text-white mb-5">Sube el nivel</h3>
            <ul className="space-y-2">
              {data.extras.map((e) => (
                <li key={e.id} className="flex items-center gap-2 text-white/85">
                  <Sparkles className="w-4 h-4 text-arre-red flex-shrink-0" /> {e.label}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* QUOTE FORM */}
      <section id="parr-quote-form" className="py-24 px-6 md:px-12 max-w-5xl mx-auto" data-testid="parrillada-quote-section">
        <div className="text-xs tracking-[0.35em] uppercase text-arre-red mb-3">⟡ Cotización</div>
        <h2 className="font-display text-5xl md:text-6xl tracking-wider uppercase text-white leading-none mb-3">
          Cotiza tu<br /><span className="text-arre-red">parrillada</span>
        </h2>
        <p className="text-white/60 max-w-xl mb-10">
          Llena el form y en menos de 24h te confirmamos disponibilidad y cotización final, compare.
        </p>

        {result ? (
          <div className="bg-arre-surface border border-arre-red p-8" data-testid="parrillada-quote-success">
            <div className="text-arre-red font-display text-3xl tracking-wider uppercase mb-3">¡Listo, compare!</div>
            <p className="text-white/85 mb-5">{result.message}</p>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-[10px] tracking-widest uppercase text-white/50">Por persona</div>
                <div className="font-display text-2xl text-white">{fmtMXN(result.price_per_person)}</div>
              </div>
              <div>
                <div className="text-[10px] tracking-widest uppercase text-white/50">Estimado total</div>
                <div className="font-display text-2xl text-white">{fmtMXN(result.estimated_total)}</div>
              </div>
            </div>
            <button
              onClick={() => { setResult(null); setForm({ ...form, contact_name: "", contact_email: "", contact_phone: "", message: "" }); }}
              className="mt-6 text-arre-red text-xs tracking-widest uppercase hover:underline"
            >
              Hacer otra cotización
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-6 bg-arre-surface border border-arre-border p-6 md:p-8">
            {/* Selection summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pb-6 border-b border-arre-border">
              <div>
                <label className="text-[10px] tracking-[0.25em] uppercase text-white/50 block mb-2">Paquete</label>
                <select value={pkgId} onChange={(e) => setPkgId(e.target.value)} className="w-full bg-arre-black border border-arre-border px-3 py-3 text-white focus:outline-none focus:border-arre-red" data-testid="quote-pkg-select">
                  {data.packages.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] tracking-[0.25em] uppercase text-white/50 block mb-2">Complementos</label>
                <select value={comp} onChange={(e) => setComp(e.target.value)} className="w-full bg-arre-black border border-arre-border px-3 py-3 text-white focus:outline-none focus:border-arre-red" data-testid="quote-comp-select">
                  <option value="3">3 complementos</option>
                  <option value="5">5 complementos</option>
                  <option value="8">8 complementos</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] tracking-[0.25em] uppercase text-white/50 block mb-2"># Personas (mín {data.min_personas})</label>
                <input
                  type="number"
                  min={1}
                  max={1000}
                  value={personas}
                  onChange={(e) => setPersonas(parseInt(e.target.value) || 0)}
                  className="w-full bg-arre-black border border-arre-border px-3 py-3 text-white focus:outline-none focus:border-arre-red"
                  data-testid="quote-personas"
                />
                {personas > 0 && personas < data.min_personas && (
                  <p className="text-arre-red text-xs mt-1" data-testid="personas-error">
                    Mínimo {data.min_personas} personas, compare.
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between bg-arre-black p-4 border border-arre-red">
              <div>
                <div className="text-[10px] tracking-widest uppercase text-white/50">Estimado total</div>
                <div className="font-display text-3xl text-white">{fmtMXN(estimatedTotal)}</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] tracking-widest uppercase text-white/50">Por persona</div>
                <div className="font-display text-2xl text-arre-red">{fmtMXN(pricePerPerson)}</div>
              </div>
            </div>

            {/* Contact */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input label="Nombre completo" value={form.contact_name} onChange={(v) => setForm({ ...form, contact_name: v })} testid="quote-name" />
              <Input label="WhatsApp" value={form.contact_phone} onChange={(v) => setForm({ ...form, contact_phone: v })} testid="quote-phone" />
              <div className="md:col-span-2">
                <Input label="Correo" type="email" value={form.contact_email} onChange={(v) => setForm({ ...form, contact_email: v })} testid="quote-email" />
              </div>
            </div>

            {/* Event */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-[10px] tracking-[0.25em] uppercase text-white/50 block mb-2">Fecha del evento</label>
                <input type="date" min={new Date(Date.now()+7*86400000).toISOString().split("T")[0]} value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} className="w-full bg-arre-black border border-arre-border px-3 py-3 text-white focus:outline-none focus:border-arre-red" data-testid="quote-date" />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.25em] uppercase text-white/50 block mb-2">Hora</label>
                <input type="time" value={form.event_time} onChange={(e) => setForm({ ...form, event_time: e.target.value })} className="w-full bg-arre-black border border-arre-border px-3 py-3 text-white focus:outline-none focus:border-arre-red" data-testid="quote-time" />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.25em] uppercase text-white/50 block mb-2">Estado / Zona</label>
                <select value={form.location_state} onChange={(e) => setForm({ ...form, location_state: e.target.value })} className="w-full bg-arre-black border border-arre-border px-3 py-3 text-white focus:outline-none focus:border-arre-red" data-testid="quote-state">
                  {STATES_MX.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>

            <Input label="Dirección del evento (opcional)" value={form.location_address} onChange={(v) => setForm({ ...form, location_address: v })} testid="quote-address" />

            {needsViaticos && (
              <div className="p-3 border border-arre-red/40 bg-arre-red/10 text-sm text-white flex items-start gap-2" data-testid="viaticos-warning">
                <MapPin className="w-4 h-4 text-arre-red flex-shrink-0 mt-0.5" />
                <div>Tu zona ({form.location_state}) requiere viáticos extra (transporte, hospedaje, equipo). Te los cotizamos junto con el paquete.</div>
              </div>
            )}

            {/* Extras */}
            <div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-white/50 mb-2">Extras (opcional)</div>
              <div className="flex flex-wrap gap-2">
                {data.extras.map((e) => (
                  <button
                    key={e.id}
                    type="button"
                    onClick={() => toggleExtra(e.id)}
                    className={`px-4 py-2 border text-xs tracking-widest uppercase ${
                      form.extras.includes(e.id) ? "bg-arre-red border-arre-red text-white" : "bg-arre-black border-arre-border text-white/70 hover:border-white/40"
                    }`}
                    data-testid={`extra-${e.id}`}
                  >
                    {e.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase text-white/50 block mb-2">Notas / detalles</label>
              <textarea
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                rows={3}
                placeholder="¿Cumpleaños? ¿Boda? ¿Algún corte específico que quieras incluir?"
                className="w-full bg-arre-black border border-arre-border px-3 py-3 text-white focus:outline-none focus:border-arre-red"
                data-testid="quote-message"
              />
            </div>

            {err && <div className="p-3 border border-arre-red bg-arre-red/10 text-sm text-white" data-testid="quote-error">{err}</div>}

            <button
              type="submit"
              disabled={submitting}
              className="btn-arre w-full bg-arre-red hover:bg-arre-red-dark text-white font-display text-xl tracking-widest uppercase py-4 disabled:opacity-50"
              data-testid="quote-submit"
            >
              {submitting ? "Enviando..." : "Solicitar cotización →"}
            </button>
            <p className="text-[11px] text-white/40 text-center">En menos de 24h te confirmamos disponibilidad y precio final.</p>
          </form>
        )}
      </section>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="text-[10px] tracking-[0.25em] uppercase text-white/50 block mb-2">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-arre-black border border-arre-border px-3 py-3 text-white focus:outline-none focus:border-arre-red"
        data-testid={testid}
      />
    </div>
  );
}
